import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { motion } from "framer-motion";
import { Sun, Moon, ArrowLeft } from "@phosphor-icons/react";

export default function FairUsePolicyPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="fair-use-page">
      <nav className="sticky top-0 z-50 bg-[var(--bg-base)]/80 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          <button onClick={toggleTheme} className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] transition-colors">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-3">
            Fair Use Policy
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-10">Last updated: April 2026</p>

          <div className="space-y-8 text-[var(--text-secondary)]">
            <Section title="What is Fair Use?">
              <p>
                Our Studio and Agency plans offer unlimited AI content generation, subject to a fair use limit of <strong className="text-[var(--text-primary)]">10,000 generations per month</strong>. This limit ensures reliable, fast service for all users while giving you the freedom to produce content at scale.
              </p>
            </Section>

            <Section title="How It Works">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="text-[var(--text-primary)]">Under 8,000 generations:</strong> No restrictions. Generate freely.
                </li>
                <li>
                  <strong className="text-amber-400">At 8,000 generations:</strong> You'll receive an in-app notification that you're approaching the fair use limit. No action required — just a heads up.
                </li>
                <li>
                  <strong className="text-red-400">At 10,000 generations:</strong> Generation pauses until your billing cycle resets. Your existing content, history, templates, and resources remain fully accessible.
                </li>
              </ul>
            </Section>

            <Section title="What Counts as a Generation?">
              <p>
                Each AI-powered content creation action counts as one generation. This includes:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Creating a new piece of content (any type)</li>
                <li>Repurposing content to a new format</li>
                <li>Regenerating a section of existing content</li>
                <li>Generating a content brief via the Brief Builder</li>
                <li>Extracting brand voice via the AI wizard</li>
                <li>Generating a monthly content plan</li>
              </ul>
              <p className="mt-2">
                Editing, saving, publishing, sharing, and exporting do <strong className="text-[var(--text-primary)]">not</strong> count as generations.
              </p>
            </Section>

            <Section title="Need More?">
              <p>
                If your workflow regularly exceeds 10,000 generations per month, contact us. We're happy to discuss custom limits tailored to your usage.
              </p>
            </Section>

            <Section title="Why This Policy Exists">
              <p>
                AI generation has real compute costs. Fair use prevents abuse while keeping plans affordable for the 99% of users who will never come close to the limit. We believe in transparency — no hidden throttling, no surprise charges.
              </p>
            </Section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)] mb-3">{title}</h2>
      <div className="text-sm leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
